"use client";

import { useState, useCallback } from "react";
import { CheckCircle, Clock, Loader2, MessageSquare } from "lucide-react";
import type { DealContractReview } from "@/lib/deals/contract-review/types";
import { ContractPreview } from "@/components/deals/contract/ContractPreview";
interface ContractDraftReviewProps {
  dealId: string;
  role: "seller" | "buyer";
  contractReview: DealContractReview | null;
  onAccepted?: (review: DealContractReview) => void;
}

/**
 * ContractDraftReview - Shows the contract draft for review and acceptance.
 *
 * Displayed when deal.status === "contract_review".
 * Both buyer and seller see the contract PDF + acceptance button + feedback textarea.
 * When both parties accept, the deal auto-advances to awaiting_signatures.
 */
export function ContractDraftReview({
  dealId,
  role,
  contractReview,
  onAccepted
}: ContractDraftReviewProps) {
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const myAccepted = role === "seller" ? contractReview?.seller_contract_accepted === true : contractReview?.buyer_contract_accepted === true;
  const otherAccepted = role === "seller" ? contractReview?.buyer_contract_accepted === true : contractReview?.seller_contract_accepted === true;
  const otherLabel = role === "seller" ? "Käufer" : "Verkäufer";
  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/deals/${dealId}/contract-review/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          accepted: true
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Fehler beim Akzeptieren");
      }
      const data_0 = await res.json();
      onAccepted?.(data_0.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    } finally {
      setSubmitting(false);
    }
  }, [dealId, onAccepted]);
  const handleFeedback = useCallback(async () => {
    if (!feedback.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res_0 = await fetch(`/api/deals/${dealId}/contract-review/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          accepted: false,
          feedback: feedback.trim()
        })
      });
      if (!res_0.ok) {
        const data_1 = await res_0.json().catch(() => ({}));
        throw new Error(data_1.error || "Fehler beim Senden");
      }
      setFeedbackSent(true);
      setFeedback("");
      setShowFeedback(false);
    } catch (err_0) {
      setError(err_0 instanceof Error ? err_0.message : "Unbekannter Fehler");
    } finally {
      setSubmitting(false);
    }
  }, [dealId, feedback]);
  return <div className="space-y-4">
      {/* Contract document preview */}
      <ContractPreview previewUrl={contractReview?.contract_document_url || contractReview?.contract_preview_url} generatedAt={contractReview?.contract_uploaded_at || contractReview?.contract_preview_generated_at} dealId={dealId} />

      {/* Acceptance status of other party */}
      {otherAccepted && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <p className="text-sm text-emerald-700">
            {otherLabel} hat den Vertragsentwurf akzeptiert.
          </p>
        </div>}

      {/* User's acceptance state */}
      {myAccepted ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-emerald-700">Vertragsentwurf akzeptiert</p>
            <p className="text-sm text-emerald-600 mt-1">
              {otherAccepted ? "Beide Parteien haben den Vertrag akzeptiert. Der Vertrag wird zur Unterschrift vorbereitet." : `Warten auf Zustimmung des ${otherLabel}s.`}
            </p>
          </div>
        </div> : <div className="space-y-3">
          {/* Accept button */}
          <button onClick={handleAccept} disabled={submitting} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Vertragsentwurf akzeptieren
          </button>

          {/* Feedback toggle */}
          {!showFeedback && !feedbackSent && <button onClick={() => setShowFeedback(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors">
              <MessageSquare className="w-4 h-4" />
              Feedback oder Änderungswunsch mitteilen
            </button>}

          {/* Feedback textarea */}
          {showFeedback && <div className="rounded-lg border border-zinc-200 p-4 space-y-3">
              <p className="text-sm font-medium text-zinc-700">Feedback an unser Team</p>
              <textarea value={feedback} onChange={e => setFeedback(e.target.value)} maxLength={2000} rows={3} placeholder="Beschreiben Sie Ihre Änderungswünsche..." className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none" />
              <div className="flex gap-2">
                <button onClick={handleFeedback} disabled={submitting || !feedback.trim()} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-zinc-900 text-white rounded-lg font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
                  Feedback senden
                </button>
                <button onClick={() => {
            setShowFeedback(false);
            setFeedback("");
          }} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700 transition-colors">
                  Abbrechen
                </button>
              </div>
            </div>}

          {/* Feedback sent confirmation */}
          {feedbackSent && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                Feedback gesendet. Unser Team wird sich melden.
              </p>
            </div>}
        </div>}

      {/* Error display */}
      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>}
    </div>;
}